import React from 'react';
import {useState} from "react";
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { cloneDeep, forEach } from 'lodash';

import { toggleSelected, toggleExpanded, selectAllNodesGraphed, getSelectedNodes, setSelectedNodes } from '../../store/positions/menuNodesSlice';

import { fetchResolvedRules } from '../../store/rules/rules';
import { RecursiveNodes } from './RecursiveNodes';

import Grid from '@material-ui/core/Grid';
import { getFilterStatus, setOffline, setOnline } from '../../store/positions/filterStatusSlice';

const StyledMenu = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  background: #FFF
  
  ::-webkit-scrollbar {
    display: none;
  }
  
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const Search = styled.div`
  width: 100%;
  height: 55px;
  background: #FFF;
  border-bottom: 1px solid #ddd;
`;

const StyledTextInput = styled.input`
  font-size: 16px;
  border: 1px solid #ddd;
  padding: 5px;
  margin: 10px auto;
  width: 90%;
  display: block;
  text-align: center;
  color: #333;
  
  &:disabled {
    background: #FFF;
    color: #555;
  }
   
`;

const DeselectAll = styled.a<{onClick: any; enabled: any;}>`
  ${props => props.enabled && `
    pointer-events: none;
    opacity: 0.5;
  `}
  display: block;
  margin 1.25rem auto 1rem auto;
  width: 80%;
  min-width: 50px;
  padding: 5px 10px;
  border: 1px solid #1F3A7D;
  background: #FFFFFF;
  font-size: 12px;
  text-align: center;
  border-radius: 10px;
  color: #233567;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  text-decoration: none;
  text-align: center;

  &:hover {
    cursor: pointer;
    color: #FFFFFF;
    background: #233567;
  }
`;



const FilterButton = styled.a<{onClick: any; active: boolean;}>`
  display: block;
  margin: 0.5rem 14px 0rem 14px;
  padding: 5px 10px;
  border: 1px solid #1F3A7D;
  background: #FFFFFF;
  font-size: 12px;
  text-align: center;
  border-radius: 4px;
  color: #233567;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.3s, color 0.3s;

  &:hover {
    cursor: pointer;
    color: #FFFFFF;
    background: #233567;
  }

  ${props => props.active && `
    && {
      color: #FFFFFF;
      background: #233567;
    }
    &&&:hover {
      opacity: 60%;
      // cursor: pointer;
      // color: #233567;
      // background: #FFFFFF;
    }
  `}
`;

const TextInput = ({onChange }: { onChange?: any }) => {
  return (
    <StyledTextInput type="text" placeholder="Search" onChange={onChange}/>
  )
}

export const PowerSingleSelectPositionMenu = ({ path }: { path?: string }) => {
  
  const menuNodes = useSelector(selectAllNodesGraphed); 
    
  const dispatch = useDispatch();  
  const [query, setQuery] = useState("");
  
  const onNodeClick = (node, e) => {    
    const location = window.location.toString();
        if (location.includes('/devices')) {
          dispatch(toggleSelected({id: node.id, ctrlKey: true}));
          dispatch(fetchResolvedRules());
        } else {
          if (!node.type.includes('site') && !node.type.includes('project')) {
            dispatch(toggleSelected({id: node.id, ctrlKey: false}));
            dispatch(fetchResolvedRules());
        }
    }    
  }
  
  const onNodeExpandClick = (node, e) => {
    dispatch(toggleExpanded({id: node.id}));
  }
  /**
   * This expression will return matching project or site array 
   * for the given text input (id/label) by the user
   * 
   * @param     {Array}        groupedNodes   Array of objects containing projects or sites 
   * @param     {string}       type           States whether it is a project or a site
   * 
   * @returns   {Array}
   */
  const searchTopLevel = (groupedNodes: Array<any>, type: string) => {
    
    if (type === 'project'){
      var format_query = 'p-' + query.toLowerCase();
    }
    else{
      var format_query = 's-' + query.toLowerCase();
    }
    // Keyword is checked against the title
    const labelNodes = groupedNodes.filter(node => node.label.toLowerCase().includes(query.toLowerCase()))
    
    // Keyword is checked against the id    
    const idNodes = groupedNodes.filter(node => node.id.toLowerCase().includes(format_query))
    
    // If both the arrays are identical just use one array
    if ((labelNodes.length && idNodes.length) && JSON.stringify(labelNodes) === JSON.stringify(idNodes)) return idNodes   
    else
    { 
      // Else extract unique elements from both the arrays, by doing this it will eliminate duplicating same elements  
      var intersects = labelNodes.filter(x => idNodes.includes(x)); 
      if (!intersects.length) return labelNodes.concat(idNodes)
      else{
        var resta = labelNodes.filter(x => !intersects.includes(x)); 
        var restb = idNodes.filter(x => !intersects.includes(x));       
        
        return intersects.concat(resta, restb)
      }
      
    }     
  }

  /**
   * This expression will return matching positions for the given text input (id/label/monitor_serial) by the user
   * @param     {Array}        groupedNodes   Array of objects containing positions
   * @returns   {Array} 
   */
  const searchPositionsLevel = (groupedNodes) => {
    // Keyword is checked against the title
    const labelMatchedNodes = groupedNodes.filter(node => node.label.toLowerCase().includes(query.toLowerCase()))    

    // Keyword is checked against the id
    const idMatchedNodes = groupedNodes.filter(node => node.id.toLowerCase().includes(query.toLowerCase()))
    
    // Keyword is checked against the monitor serial
    const serialMatchedNodes = groupedNodes.filter(node => node.monitor_serial.toLowerCase().includes(query.toLowerCase()))
   
    // If all the arrays are identical just use one array
    if ((labelMatchedNodes.length && idMatchedNodes.length && serialMatchedNodes.length) && 
      ((JSON.stringify(labelMatchedNodes) === JSON.stringify(idMatchedNodes)) || (JSON.stringify(labelMatchedNodes) === JSON.stringify(serialMatchedNodes)) || (JSON.stringify(idMatchedNodes) === JSON.stringify(serialMatchedNodes)))) {
        return idMatchedNodes 
    }
    else
    { 
      // Else extract unique elements from all arrays, by doing this it will eliminate duplicating same elements  
      
      // First concat all three object arrays
      const concat_array = labelMatchedNodes.concat(idMatchedNodes, serialMatchedNodes);

      // Define empty arrays to temporarily hold `distinct_nodes` and `intersect_nodes`
      var distinct_nodes = [];
      var intersect_nodes = [];
      
      // Iterate over concat_array to find intersect points
      concat_array.map(node => {     

        // Checks if `distinct_nodes` array is not empty
        if(distinct_nodes.length){

          // Checks if we add the same node to `distinct_nodes` array, this returns intersect nodes
          const inter_nodes = distinct_nodes.filter(intersect_node => intersect_node.id.includes(node.id))
          
          // If no intersect node is identified correspond to the mapping node, add that node to `distinct_nodes` array
          if(!inter_nodes.length){
            distinct_nodes.push(node)
          }
          // Else do not add that node to the `distinct_nodes`
          else{
            // Before adding to the `intersect_nodes`, check if the mapping node is already exist in `intersect_nodes`

            inter_nodes.map(inter_node => {
              const mid_in = intersect_nodes.filter(mid_val_node => mid_val_node.id.includes(inter_node.id))
              // If it does not overlap, append `intersect_nodes
              if (!mid_in.length){
                intersect_nodes.push(inter_node)
              }              
            })
          }
        }
        // If it is empty append the node to `distinct_nodes
        else{          
          distinct_nodes.push(node)
        }
      })

      // If there are no intersect nodes in `concat_array` return `concat_array`
      if (!intersect_nodes.length) return concat_array;

      // Else, return a non overlapping array 
      else return distinct_nodes;             
    }  
  } 

  /**
   * This expression will do a tree search for menuNodes. (Note: elements in menuNodes have nested objects)
   *  
   * @param   {Array}     groupedNodes         Array of objects containing `menuNodes`
   * @param   {number}    project_length       
   * @returns {Array}
   */

  const searchMapping = (groupedNodes, project_length) => {
    let potential_nodes = []
    let temp_sites =[]

    groupedNodes.map(projectnodes => {   
      var type ='site'
      
      // Checks if one of the sites have the searched keyword  
      const sites = searchTopLevel(projectnodes.nodes, type)
          
      // If it does, add the relevant site's project name to the temporary list
      if (sites.length) {        
        let temp_projectnode = cloneDeep(projectnodes)
        temp_projectnode.nodes = cloneDeep(sites)
           
        // Also checks if any stations have the searched keyword        
        temp_projectnode.nodes.map(sitenodes => {
          
          var position = searchPositionsLevel(sitenodes.nodes)

          if(position.length) {
            sitenodes.nodes = cloneDeep(position)
          }
        })
        
        potential_nodes.push(temp_projectnode)
        temp_sites.push(sites)
        
      }
      
      // If it does not, check if any station have the searched query
      else{
        var potential_sites = []
        let temp_position = []
        projectnodes.nodes.map(sitenodes => {
          
          var position = searchPositionsLevel(sitenodes.nodes)

          // If it does, add the relevant station's site to the temporary list
          if(position.length) {
            
            let temp_sitenode = cloneDeep(sitenodes)
            temp_sitenode.nodes = cloneDeep(position)
            potential_sites.push(temp_sitenode)
            temp_position.push(position)                
          }
        })
        if (temp_position.length){              
          var temp_projectnode = cloneDeep(projectnodes)
          temp_projectnode.nodes = cloneDeep(potential_sites)
          potential_nodes.push(temp_projectnode) 
        }
        if(project_length && !temp_position.length){
          potential_nodes.push(projectnodes)
        }
        
      }
    })  
    
    return potential_nodes
  }

  const searchFilter = () => {
    
    // If query is empty return the entire array
    if (query==='') return menuNodes    
    else {     
      // Check if the searched keyword exist in project label or id
      var type = 'project'
      const projects = searchTopLevel(menuNodes, type);     
      
      // When the searched term is not inside the project list --> do the map search
      if (!projects.length)return searchMapping(menuNodes, projects.length).sort((a,b)=> (a.label > b.label ? 1 : -1))

      
      else{    
        // If the search term is in project label or id,   
        // Get the difference from the original array, --> do the map search, incase not to miss a project, if the sites or stations have the key word
        const difference = menuNodes.filter(x => !projects.includes(x));         
        const rest_nodes = searchMapping(difference, !projects.length);
        
        // Do the map search to earlier filtered projects to find whether sites or stations have the key term
        const project_nodes = searchMapping(projects, projects.length)
        
        // Concat the generated result and display
        return rest_nodes.concat(project_nodes).sort((a,b)=> (a.label > b.label ? 1 : -1))
      }
    }
    
  }
  
  const filteredNodes = searchFilter();
  const isDisabled = useSelector(getSelectedNodes).length > 0? false : true; 
  const resetSelectedNodes = () => {
    dispatch(setSelectedNodes({ids: ["none"]}));
  }

  const currentStatus = useSelector(getFilterStatus);
  
  return (
    <StyledMenu>
      {false &&<Search/>}
      <TextInput onChange={event => setQuery(event.target.value)} />
      <Grid container spacing={0}>
        <Grid item xs={6}>
          <FilterButton
            active={currentStatus === 'online' || currentStatus === 'all'}
            onClick={() => dispatch(setOnline())}
          >
            {/* {console.log('Online button active: ', currentStatus === 'online')} */}
            Online
          </FilterButton>
        </Grid>
        <Grid item xs={6}>
          <FilterButton
            active={currentStatus === 'offline' || currentStatus === 'all'}
            onClick={() => dispatch(setOffline())}
          >
            Offline
          </FilterButton>
        </Grid>
      </Grid>
      <DeselectAll enabled={isDisabled} onClick={() => {resetSelectedNodes()}}>Deselect All</DeselectAll>
      <RecursiveNodes menuNodes={filteredNodes} onNodeClick={onNodeClick} onNodeExpandClick={onNodeExpandClick}/>
    </StyledMenu>
  );
}


